/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { Link } from 'react-router';
import { ChevronRightIcon } from '@/shared/ui/radix-icons';
import { Text } from '@/shared/ui';

/** One indicator: a number, what it counts, and where it continues. */
export interface Tile {
  id: string;
  /** Already formatted for reading — an em dash when there is no answer. */
  value: string;
  /** Sits on the number's baseline: "vagos", "dias", "abertos". */
  unit?: string;
  label: string;
  /** Where the nuance lives: the total, the window, what the number misses. */
  hint: string;
  accent: string;
  /**
   * The list this number opens, filtered. Undefined means the filter does
   * not exist yet, and the tile is then not a link at all — see below.
   */
  to?: string;
}

/*
 * A column of three lines on a wide screen; one line on a phone.
 *
 * Two tiles sharing 343px leave each hint about 145px to wrap in, which
 * turned "Parcelas vencidas sem baixa registrada · R$ 31.200 · a mais
 * antiga há 81 dias" into five lines and the card into 170px of height.
 * Laid sideways the number keeps a fixed column at the left and the hint
 * gets the width of the screen, so the same sentence is two lines and the
 * tile is a list row. Fourteen of these are shorter stacked than they were
 * in pairs -- the pairs were never two tiles tall, they were two ragged
 * columns of five-line prose.
 */
const CONTENT = 'flex flex-1 min-w-0 flex-col gap-[2px] max-sm:flex-row max-sm:items-center max-sm:gap-(--space-3)';

/**
 * The inside of a tile, identical whether or not it happens to be a link.
 *
 * The number is the heaviest thing here and the label sits under it: read
 * in the other order, the eye has to cross a line of prose before it
 * reaches the figure it came for, and nine of those is the scrolling the
 * redesign exists to remove.
 */
function TileBody({ tile, large }: { tile: Tile; large?: boolean }) {
  return (
    <>
      <Text
        as="span"
        className="w-[3px] flex-none rounded-(--radius-full)"
        style={{ background: tile.accent }}
      />
      <Text as="span" className={CONTENT}>
        <Text as="span" className="flex items-baseline gap-(--space-1) max-sm:min-w-[2.75rem] max-sm:shrink-0">
          <Text
            as="span"
            /*
             * A step smaller on a phone, where two tiles share 343px. At the
             * desktop size a formatted currency total ("R$ 44,6 mi") is wider
             * than its own tile and pushes the card into horizontal overflow
             * — the number stops being readable precisely on the screen that
             * has least room to spare.
             */
            className={`${large ? 'text-[1.75rem] sm:text-[2.35rem]' : 'text-[1.4rem] sm:text-[1.75rem]'} [font-weight:var(--font-bold)] leading-[1.05] tracking-[-0.02em] text-[var(--text-primary)] tabular-nums`}
          >
            {tile.value}
          </Text>
          {tile.unit && (
            <Text
              as="span"
              className="text-(length:--text-body-sm) font-[var(--font-semibold)] text-[var(--text-secondary)]"
            >
              {tile.unit}
            </Text>
          )}
        </Text>
        {/* Its own column so the pair travels together when the tile turns
            sideways. The 2px gap matches the parent's, so nothing about the
            wide-screen stack changes. */}
        <Text as="span" className="flex min-w-0 flex-1 flex-col gap-[2px]">
          <Text
            as="span"
            className="text-(length:--text-body-sm) font-[var(--font-semibold)] leading-[1.3] text-[var(--text-primary)]"
          >
            {tile.label}
          </Text>
          <Text
            as="span"
            className="text-(length:--text-meta) leading-[1.35] text-[var(--text-tertiary-soft)]"
          >
            {tile.hint}
          </Text>
        </Text>
      </Text>
    </>
  );
}

const SHELL = 'flex items-stretch gap-(--space-3) rounded-(--radius-lg) border border-(--border-subtle) bg-[var(--surface-raised)] text-left min-w-0';

/**
 * Two sizes, for two densities of screen.
 *
 * The compact one is sized so four tiles share a row under fourteen
 * numbers without a label wrapping past two lines. The roomy one is for
 * the shortened screens, where six numbers or four have the width of the
 * page to themselves and would read as undersized at the compact padding.
 *
 * Neither is stretched to match anything. Making a card taller than its
 * contents to line its column up with the one beside it produces a card
 * that looks unfinished, which is a worse defect than an uneven bottom
 * edge -- the shortened screens avoid the problem by not putting the
 * tiles beside the panel at all. See `Dashboard`.
 */
const PADDING = {
  default: 'px-(--space-3-5) py-(--space-3)',
  large: 'px-(--space-5) py-(--space-5)',
};

/**
 * One indicator, as a whole click target when it has somewhere to go.
 *
 * A tile whose filter does not exist yet renders as plain text: no chevron,
 * no hover, not focusable. The alternative — linking to the unfiltered list
 * — answers "seven listings cannot be published" with three hundred
 * properties and no way to tell which seven, which is worse than not
 * offering the click at all. The missing filters are inventoried in
 * `brainiac/reports/espec-backend-visao-geral_2026-09-14.md`.
 *
 * @param props.tile - The indicator to render
 */
export function OverviewTile({ tile, large }: { tile: Tile; large?: boolean }) {
  const shell = `${SHELL} ${large ? PADDING.large : PADDING.default}`;

  if (!tile.to) {
    return (
      <div className={shell} data-testid={`overview-tile-${tile.id}`} data-linked="false">
        <TileBody tile={tile} large={large} />
      </div>
    );
  }

  return (
    <Link
      to={tile.to}
      data-testid={`overview-tile-${tile.id}`}
      data-linked="true"
      className={`${shell} no-underline transition-shadow duration-150 hover:border-(--border-medium) hover:shadow-[var(--shadow-md)]`}
    >
      <TileBody tile={tile} large={large} />
      <Text as="span" className="flex flex-none items-center text-[var(--text-tertiary-soft)]">
        <ChevronRightIcon width={14} height={14} />
      </Text>
    </Link>
  );
}

const HEADING = 'text-(length:--text-app-section) font-[var(--font-bold)] text-[var(--text-primary)]';

/**
 * The group heading, rendered invisibly, to start a neighbouring column's
 * cards at the same line as this column's.
 *
 * Mirroring the real heading rather than hard-coding its height: the
 * offset then follows the type, and cannot drift the day the heading
 * changes size.
 */
export function GroupHeadingSpacer() {
  return (
    <Text as="span" aria-hidden="true" className={`${HEADING} invisible`}>
      &nbsp;
    </Text>
  );
}

/** A section of the overview: its heading and the cards chosen under it. */
export interface TileGroup {
  id: string;
  title: string;
  tiles: Tile[];
}

/**
 * One group: its heading and its tiles.
 *
 * The cards are a choice, but the sections are not: without the headings
 * fourteen numbers read as one undifferentiated wall. A section nobody
 * chose a card for is not drawn, so there is never a heading over nothing.
 *
 * Four columns on the roomy screen, filled or not. Left to auto-fit, a
 * group of two would stretch each tile across half the page -- an 850px
 * card carrying a number and one line -- and two groups would disagree
 * about how wide a tile is. On the compact screen the 185px minimum is the
 * widest at which four still share one row. One column on a phone,
 * explicitly: `auto-fit` needs 382px to seat two and a phone hands it 343,
 * so the tile turns sideways instead.
 *
 * @param props.group - The group to render
 * @param props.large - Roomier tiles, for a screen with fewer of them
 */
export function OverviewGroup({ group, large }: { group: TileGroup; large?: boolean }) {
  return (
    <div className="flex min-w-0 flex-col gap-(--space-2)" data-testid={`overview-group-${group.id}`}>
      <Text as="span" className={HEADING}>
        {group.title}
      </Text>
      <div className={`grid gap-(--space-2) grid-cols-1 sm:gap-(--space-3) ${large
        ? 'sm:[grid-template-columns:repeat(auto-fit,minmax(260px,1fr))] min-[1100px]:grid-cols-4'
        : 'sm:[grid-template-columns:repeat(auto-fit,minmax(185px,1fr))]'}`}
      >
        {group.tiles.map((tile) => (
          <OverviewTile key={tile.id} tile={tile} large={large} />
        ))}
      </div>
    </div>
  );
}
